import { Link, useRouteError } from "react-router";
import { t } from "@lingui/core/macro";
import { Button, Result } from "antd";
import { Trans } from "@lingui/react/macro";
import { match } from "ts-pattern";
import { FallbackProps, getErrorMessage } from "react-error-boundary";
import React, { useEffect } from "react";

import permissionDeniedIllustration from "./permission-denied.svg?url";
import unexpectedError from "./unexpected-error.svg?url";
import { AppLayout } from "../layout/app-layout";
import { useWebAnalytics } from "../../contexts/web-analytics";

export function ApplicationErrorPage() {
  const error = useRouteError();

  if (error instanceof Error && error?.message?.includes("dynamically imported module")) {
    throw error;
  }

  const { reportError } = useWebAnalytics();

  const isPermissionDenial = isRouteErrorWithStatus(error, 403);

  useEffect(() => {
    if (!isPermissionDenial) {
      reportError(error);
    }
  }, [error, isPermissionDenial, reportError]);

  const props = isPermissionDenial
    ? {
        icon: (
          <img
            width="250"
            className="inline"
            src={permissionDeniedIllustration}
            alt=""
            aria-hidden
          />
        ),
        title: t`Permission Denied`,
        subTitle: t`It seems like you don't have sufficient permissions. If you think you should have, please contact the responsible person.`,
      }
    : {
        icon: <img width="250" className="inline" src={unexpectedError} alt="" aria-hidden />,
        title: t`Unexpected Error`,
        subTitle: t`An unexpected error occurred.`,
      };

  return (
    <AppLayout name={props.title}>
      <Result
        className="pt-32"
        extra={
          <Link to={"/"}>
            <Button type="link">
              <Trans>Back to home page</Trans>
            </Button>
          </Link>
        }
        {...props}
      />
    </AppLayout>
  );
}

function isRouteErrorWithStatus(error: unknown, status: number) {
  return match(error)
    .with({ status }, () => true)
    .otherwise(() => false);
}

export function ErrorPage({ error }: FallbackProps) {
  if (getErrorMessage(error)?.includes("dynamically imported module")) {
    window.location.reload();
    return null;
  }

  return (
    <div className="h-screen flex justify-center items-center">
      <Trans>
        Error loading page.
        <br />
        Please try again later
      </Trans>
    </div>
  );
}
